import { getSupabase, isSupabaseConfigured } from '../lib/supabase';
import { formatApiError } from '../lib/apiError';
import { waitForSupabaseSession } from '../lib/authSession';
import { getNationality } from '../lib/onboardingStorage';
import { buildConsentTimestampFields } from '../lib/legalConsentVersion';
import { assertOnlineOrThrow, isAppOnline } from '../lib/offlineGuard';
import { isLoggedInStorage, loadNickname, loadLanguage } from '../lib/onboardingStorage';
import { apiOk, apiErr } from '../lib/apiEnvelope';
import { sendToAutomation } from '../lib/sendToAutomation';
import { getCountryCodeForCity } from './regionWaitlistApi';
import { normalizeStoredPhone } from '../lib/phoneDialCodes';
import { resolveLocaleCode } from '../locales';

export const PROFILE_UPDATED_EVENT = 'mingo-profile-updated';

export function publishProfileUpdated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
  }
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} userId
 */
async function countOwnedBusinesses(supabase, userId) {
  if (!userId) return 0;
  const { count, error } = await supabase
    .from('businesses')
    .select('id', { count: 'exact', head: true })
    .eq('owner_user_id', userId)
    .is('deleted_at', null);
  if (error) return 0;
  return count || 0;
}

/**
 * auth_id 전용 users 행이 새로 생기며 업체·아바타가 원래 행과 분리되는 경우를 복구합니다.
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {import('@supabase/supabase-js').User} authUser
 * @returns {Promise<string|null>} public.users.id
 */
export async function resolveMyPublicUserId(supabase, authUser) {
  if (!supabase || !authUser?.id) return null;

  try {
  const { data: authRow } = await supabase
    .from('users')
    .select('id, auth_id, avatar_url')
    .eq('auth_id', authUser.id)
    .maybeSingle();

  // auth_id 연결 정본 행 — 조회 시 side-effect 없이 즉시 반환
  if (authRow?.id) return authRow.id;

  let emailRows = [];
  if (authUser.email) {
    const { data, error } = await supabase
      .from('users')
      .select('id, auth_id, avatar_url')
      .eq('email', authUser.email || '');
    if (!error) emailRows = data || [];
  }

  const uniqueIds = [...new Set([authRow?.id, ...emailRows.map((r) => r.id)].filter(Boolean))];
  if (uniqueIds.length === 0) return null;

  const rowById = (id) => emailRows.find((r) => r.id === id) || (authRow?.id === id ? authRow : null);

  if (uniqueIds.length === 1) {
    const id = uniqueIds[0];
    const row = rowById(id);
    if (row && !row.auth_id) {
      await supabase.from('users').update({ auth_id: authUser.id }).eq('id', id);
    }
    return id;
  }

  const scored = await Promise.all(
    uniqueIds.map(async (id) => ({
      id,
      biz: await countOwnedBusinesses(supabase, id),
      row: rowById(id),
    })),
  );
  scored.sort((a, b) => b.biz - a.biz);
  const primary = scored[0];
  if (!primary?.id) return authRow?.id || uniqueIds[0] || null;

  const duplicateWithAuth = scored.find((s) => s.id !== primary.id && s.row?.auth_id === authUser.id);

  const { data: primaryFull } = await supabase
    .from('users')
    .select('auth_id, avatar_url')
    .eq('id', primary.id)
    .maybeSingle();

  if (!primaryFull?.auth_id || primaryFull.auth_id !== authUser.id) {
    await supabase.from('users').update({ auth_id: authUser.id }).eq('id', primary.id);
  }

  const dupAvatar = duplicateWithAuth?.row?.avatar_url;
  if (dupAvatar && !primaryFull?.avatar_url) {
    await supabase.from('users').update({ avatar_url: dupAvatar }).eq('id', primary.id);
  }

  return primary.id;
  } catch {
    const { data: byAuth } = await supabase
      .from('users')
      .select('id')
      .eq('auth_id', authUser.id)
      .maybeSingle();
    return byAuth?.id || null;
  }
}

/** 앱은 localStorage로 “로그인” 판단하지만 Supabase 세션은 비동기로 복원될 수 있음 */
function buildStorageFallbackProfile(email = '') {
  const fromEmail = email && typeof email === 'string' ? email.split('@')[0] : '';
  const nn = (loadNickname() || '').trim() || fromEmail || 'Mingo';
  return {
    id: null,
    nickname: nn,
    name: '',
    gender: '',
    handle: '',
    avatar_url: '',
    email: email || '',
    bio: '',
    contact_kakao: '',
    contact_zalo: '',
    contact_telegram: '',
    contact_whatsapp: '',
    contact_phone: '',
    residence_country: '',
    residence_city: '',
    expat_since: null,
    nationality_code: '',
    languages: [],
    language: 'ko',
    external_notifications_enabled: false,
    notify_prefs: {},
    visa_type: null,
    visa_expire_date: null,
  };
}

/**
 * getUser()가 일시적으로 빈 값을 줄 때(새로고침 직후 등) getSession()으로 폴백합니다.
 * 세션 복원 지연에 대비해 짧게 재시도합니다.
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {{ maxAttempts?: number, delayMs?: number }} [opts]
 */
export async function getAuthedSupabaseUser(supabase, opts = {}) {
  const maxAttempts = typeof opts.maxAttempts === 'number' ? opts.maxAttempts : 12;
  const delayMs = typeof opts.delayMs === 'number' ? opts.delayMs : 80;
  const session = await waitForSupabaseSession(supabase, { maxMs: 1500 });
  if (session?.user) return session.user;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const {
      data: { user: u1 },
      error: e1,
    } = await supabase.auth.getUser();
    if (!e1 && u1) return u1;
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const u2 = session?.user ?? null;
    if (u2) return u2;
    if (attempt < maxAttempts - 1) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  return null;
}

/**
 * @param {import('@supabase/supabase-js').User | null} [preloadedUser] 이미 resolve한 auth user (중복 getUser 방지)
 * @returns {Promise<object|null>} extended user row for UI
 */
/** @returns {Promise<object|null>} 로그인 사용자의 public.users 행 (미로그인 시 null) */
export async function fetchAuthUser(preloadedUser) {
  return fetchMyAppUserRow(preloadedUser);
}

export async function fetchMyAppUserRow(preloadedUser) {
  const supabase = getSupabase();
  if (!supabase) return null;
  assertOnlineOrThrow();
  const user = preloadedUser ?? (await getAuthedSupabaseUser(supabase));
  if (!user) return null;

  const publicId = await resolveMyPublicUserId(supabase, user);
  if (!publicId) return null;

  const { data, error } = await supabase.from('users').select('*').eq('id', publicId).maybeSingle();
  if (error || !data) return null;
  const langs = Array.isArray(data.languages) ? data.languages.filter(Boolean) : [];
  return {
    id: data.id,
    nickname: data.nickname || user.email?.split('@')[0] || 'Mingo',
    // 실명·성별·핸들: EditProfile 로드에 필요 (저장은 되지만 여기 누락이면 재진입 시 빈칸처럼 보임)
    name: typeof data.name === 'string' ? data.name : '',
    gender: typeof data.gender === 'string' ? data.gender : '',
    handle: typeof data.handle === 'string' ? data.handle : '',
    avatar_url: data.avatar_url || '',
    language: typeof data.language === 'string' && data.language.trim() ? data.language.trim().toLowerCase() : 'ko',
    visa_type: data.visa_type ?? null,
    visa_expire_date: data.visa_expire_date ?? null,
    visa_expiry_date: data.visa_expiry_date ?? data.visa_expire_date ?? null,
    manner_temperature:
      typeof data.manner_temperature === 'number' && Number.isFinite(data.manner_temperature)
        ? data.manner_temperature
        : 100,
    manner_signal_count:
      typeof data.manner_signal_count === 'number' && Number.isFinite(data.manner_signal_count)
        ? data.manner_signal_count
        : 0,
    region: typeof data.region === 'string' ? data.region : '',
    points: typeof data.points === 'number' && Number.isFinite(data.points) ? data.points : 0,
    exchange_alerts_premium: !!data.exchange_alerts_premium,
    referral_code: data.referral_code ?? null,
    premium_until: data.premium_until ?? null,
    bio: typeof data.bio === 'string' ? data.bio : '',
    contact_kakao: data.contact_kakao ?? '',
    contact_zalo: data.contact_zalo ?? '',
    contact_telegram: data.contact_telegram ?? '',
    contact_whatsapp: data.contact_whatsapp ?? '',
    contact_phone: data.contact_phone ?? '',
    residence_country: data.residence_country ?? '',
    residence_city: data.residence_city ?? '',
    expat_since: data.expat_since ?? null,
    nationality_code: data.nationality_code ?? '',
    languages: langs,
    birth_date: data.birth_date ?? null,
    terms_agreed_at: data.terms_agreed_at ?? null,
    privacy_agreed_at: data.privacy_agreed_at ?? null,
    external_notifications_enabled: data.external_notifications_enabled === true,
    notify_prefs:
      data.notify_prefs && typeof data.notify_prefs === 'object' && !Array.isArray(data.notify_prefs)
        ? data.notify_prefs
        : {},
    auto_friend_on_mutual_follow: data.auto_friend_on_mutual_follow !== false,
  };
}

/**
 * PDPL 동의 게이트용 — terms/privacy 타임스탬프만 조회.
 * @param {import('@supabase/supabase-js').User | null} [preloadedUser]
 */
export async function fetchMyLegalConsentStatus(preloadedUser) {
  const supabase = getSupabase();
  if (!supabase) return null;
  assertOnlineOrThrow();
  const user = preloadedUser ?? (await getAuthedSupabaseUser(supabase));
  if (!user) return null;

  const publicId = await resolveMyPublicUserId(supabase, user);
  if (!publicId) return null;

  const { data, error } = await supabase
    .from('users')
    .select('terms_agreed_at, privacy_agreed_at, consent_version, birth_date, age_14_confirmed')
    .eq('id', publicId)
    .maybeSingle();
  if (error || !data) return null;
  return data;
}

/**
 * Merge auth email + profile row for edit screen (missing row still returns email).
 * @returns {Promise<{ success: boolean, data: object, message: string }>}
 */
export async function fetchProfileForEdit() {
  if (typeof window !== 'undefined' && !isAppOnline() && isLoggedInStorage()) {
    return apiOk(buildStorageFallbackProfile(''), '');
  }

  const supabase = getSupabase();
  if (!supabase) {
    return isLoggedInStorage() ? apiOk(buildStorageFallbackProfile(''), '') : apiErr('NO_SB', {});
  }

  try {
    assertOnlineOrThrow();
  } catch (e) {
    if (e && e.code === 'OFFLINE' && isLoggedInStorage()) {
      return apiOk(buildStorageFallbackProfile(''), '');
    }
    return apiErr(e?.message || 'OFFLINE', { code: e?.code });
  }

  const user = await getAuthedSupabaseUser(supabase);
  if (!user) {
    return isLoggedInStorage() ? apiOk(buildStorageFallbackProfile(''), '') : apiErr('NO_USER', {});
  }

  const row = await fetchMyAppUserRow(user);
  const email = user.email || '';
  if (!row) {
    return apiOk(
      {
        id: null,
        nickname: user.email?.split('@')[0] || 'Mingo',
        name: '',
        gender: '',
        handle: '',
        avatar_url: '',
        email,
        bio: '',
        contact_kakao: '',
        contact_zalo: '',
        contact_telegram: '',
        contact_whatsapp: '',
        contact_phone: '',
        residence_country: '',
        residence_city: '',
        expat_since: null,
        nationality_code: '',
        languages: [],
        language: 'ko',
        external_notifications_enabled: false,
        visa_type: null,
        visa_expire_date: null,
      },
      '',
    );
  }
  return apiOk({ ...row, email }, '');
}

/**
 * @param {Record<string, unknown>} patch columns on public.users (snake_case keys as in DB)
 * @returns {Promise<{ success: boolean, data: object, message: string }>}
 */
export async function updateMyProfileRow(patch) {
  try {
    const supabase = getSupabase();
    if (!supabase) return apiErr('NO_SB', {});
    assertOnlineOrThrow();
    const user = await getAuthedSupabaseUser(supabase);
    if (!user) return apiErr('NO_USER', {});

    const publicId = await resolveMyPublicUserId(supabase, user);
    if (!publicId) return apiErr('PROFILE_NOT_FOUND', { code: 'PROFILE_NOT_FOUND' });

    const avatarUrl = typeof patch?.avatar_url === 'string' && patch.avatar_url.trim() ? patch.avatar_url.trim() : null;
    const profilePatch = { ...(patch && typeof patch === 'object' ? patch : {}) };
    if (typeof profilePatch.contact_phone === 'string') {
      const normalized = normalizeStoredPhone(profilePatch.contact_phone);
      profilePatch.contact_phone = normalized || null;
    }
    delete profilePatch.avatar_url;

    let avatarRow = null;
    if (avatarUrl) {
      try {
        avatarRow = await persistMyAvatarUrl(avatarUrl, { supabase, user, publicId });
      } catch (avErr) {
        const msg = avErr?.message || 'UPDATE_FAILED';
        const code = msg === 'AVATAR_URL_NOT_PERSISTED' ? 'AVATAR_URL_NOT_PERSISTED' : msg;
        return apiErr(msg, { code, details: avErr });
      }
    }

    const patchKeys = Object.keys(profilePatch).filter((k) => profilePatch[k] !== undefined);
    let data = avatarRow;
    if (patchKeys.length > 0) {
      const runUpdate = () =>
        supabase.from('users').update(profilePatch).eq('id', publicId).select('id, avatar_url').maybeSingle();

      let result = await runUpdate();
      if (!result.error && !result.data) {
        const nick = typeof profilePatch?.nickname === 'string' ? profilePatch.nickname : undefined;
        await upsertUserProfileFromAuth(nick);
        result = await runUpdate();
      }
      if (result.error) return apiErr(result.error.message || 'UPDATE_FAILED', { details: result.error });
      if (!result.data) return apiErr('PROFILE_NOT_FOUND', { code: 'PROFILE_NOT_FOUND' });
      data = result.data;
    }

    if (!data) return apiErr('PROFILE_NOT_FOUND', { code: 'PROFILE_NOT_FOUND' });

    if (avatarUrl && !data.avatar_url) {
      return apiErr('AVATAR_URL_NOT_PERSISTED', { code: 'AVATAR_URL_NOT_PERSISTED' });
    }

    const keys = patch && typeof patch === 'object' ? Object.keys(patch) : [];
    void sendToAutomation('user.profile_updated', { patchKeys: keys });
    return apiOk({ updated: true, row: data }, '');
  } catch (e) {
    if (e && e.code === 'OFFLINE') return apiErr(e.message || 'OFFLINE', { code: 'OFFLINE' });
    return apiErr(e?.message || 'UPDATE_FAILED', {});
  }
}

/**
 * Persist selected regions for current auth user.
 * Primary region is mirrored to users.residence_city / residence_country.
 * Tries RPC first (auth.uid() on DB side), then falls back to auth_id filter update.
 * @param {string[]} regions
 * @returns {Promise<{ success: boolean, data: object, message: string }>}
 */
export async function saveMySelectedRegions(regions) {
  try {
    const supabase = getSupabase();
    if (!supabase) return apiErr('NO_SB', {});
    assertOnlineOrThrow();
    const user = await getAuthedSupabaseUser(supabase);
    if (!user) return apiErr('NO_USER', {});

    const clean = Array.isArray(regions)
      ? [...new Set(regions.map((x) => String(x || '').trim()).filter(Boolean))].slice(0, 2)
      : [];
    const primary = clean[0] || null;
    const country = primary ? getCountryCodeForCity(primary) || null : null;

    // Preferred path: SQL function using auth.uid() internally.
    const { error: rpcError } = await supabase.rpc('set_my_selected_regions', {
      p_regions: clean,
      p_primary_region: primary,
      p_country_code: country,
    });
    if (!rpcError) return apiOk({ updated: true, regions: clean }, '');

    const rpcMsg = rpcError.message || 'RPC_FAILED';
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[saveMySelectedRegions] rpc failed', rpcMsg, rpcError);
    }

    // Backward-compatible fallback for environments where RPC isn't migrated yet.
    const { error } = await supabase
      .from('users')
      .update({
        residence_city: primary,
        residence_country: country,
      })
      .eq('auth_id', user.id);
    if (error) return apiErr(error.message || 'UPDATE_FAILED', { details: error, rpcError: rpcMsg });

    return apiOk({ updated: true, regions: clean, fallback: true }, '');
  } catch (e) {
    if (e && e.code === 'OFFLINE') return apiErr(e.message || 'OFFLINE', { code: 'OFFLINE' });
    return apiErr(e?.message || 'UPDATE_FAILED', {});
  }
}

/**
 * @param {string} avatarUrl
 * @param {{ supabase?: import('@supabase/supabase-js').SupabaseClient, user?: import('@supabase/supabase-js').User, publicId?: string | null }} [ctx]
 * @returns {Promise<{ id: string, avatar_url: string }>}
 */
export async function persistMyAvatarUrl(avatarUrl, ctx = {}) {
  const supabase = ctx.supabase ?? getSupabase();
  if (!supabase) throw new Error('NO_SB');
  assertOnlineOrThrow();
  await waitForSupabaseSession(supabase, { maxMs: 2000 });
  const user = ctx.user ?? (await getAuthedSupabaseUser(supabase));
  if (!user?.id) throw new Error('NO_USER');
  const publicId = ctx.publicId ?? (await resolveMyPublicUserId(supabase, user));
  if (!publicId) throw new Error('PROFILE_NOT_FOUND');

  const nextUrl = typeof avatarUrl === 'string' ? avatarUrl.trim() : '';
  if (!nextUrl) throw new Error('AVATAR_URL_EMPTY');

  const attempts = [
    { key: 'auth_id', filter: () => supabase.from('users').update({ avatar_url: nextUrl }).eq('auth_id', user.id) },
    { key: 'id', filter: () => supabase.from('users').update({ avatar_url: nextUrl }).eq('id', publicId) },
  ];

  let lastError = null;
  for (const attempt of attempts) {
    const { data, error } = await attempt.filter().select('id, avatar_url').maybeSingle();
    if (error) {
      lastError = error;
      console.warn('[profileApi] persistMyAvatarUrl', attempt.key, error.message || error);
      continue;
    }
    if (data?.avatar_url) return data;
    console.warn('[profileApi] persistMyAvatarUrl 0 rows', {
      via: attempt.key,
      authUid: user.id,
      publicId,
      urlPrefix: nextUrl.slice(0, 120),
    });
  }

  if (lastError) throw lastError;
  throw new Error('AVATAR_URL_NOT_PERSISTED');
}

export async function updateUserAvatarUrl(avatarUrl) {
  await persistMyAvatarUrl(avatarUrl);
}

/**
 * Updates visa_expiry_date only (header D-Day). Does not touch visa_type / visa_expire_date.
 * @param {string|null} visaExpiryDate YYYY-MM-DD or empty to clear
 */
export async function updateVisaExpiryDate(visaExpiryDate) {
  const ymd =
    visaExpiryDate == null || String(visaExpiryDate).trim() === ''
      ? null
      : String(visaExpiryDate).trim().slice(0, 10);
  const res = await updateMyProfileRow({ visa_expiry_date: ymd });
  if (!res.success) throw new Error(res.message || 'UPDATE_FAILED');
}

export async function updateUserVisa(visaType, visaExpireDate) {
  const supabase = getSupabase();
  if (!supabase) throw new Error('NO_SB');
  assertOnlineOrThrow();
  const user = await getAuthedSupabaseUser(supabase);
  if (!user) throw new Error('NO_USER');
  const { error } = await supabase
    .from('users')
    .update({
      visa_type: visaType || null,
      visa_expire_date: visaExpireDate || null,
    })
    .eq('auth_id', user.id);
  if (error) throw error;
}

/**
 * Upserts public.users row for the current auth user (auth_id = auth.users.id).
 * @param {string} [nickname]
 * @param {{ consentTerms?: boolean, consentPrivacy?: boolean, consentMarketing?: boolean, consentLocation?: boolean, birthDate?: string }} [consents]
 */
export async function upsertUserProfileFromAuth(nickname, consents = {}) {
  const supabase = getSupabase();
  if (!supabase) return;
  assertOnlineOrThrow();
  const user = await getAuthedSupabaseUser(supabase);
  if (!user) return;

  const linkedId = await resolveMyPublicUserId(supabase, user);

  const explicitNick = typeof nickname === 'string' && nickname.trim() ? nickname.trim() : '';

  const row = {
    auth_id: user.id,
    email: user.email || '',
    is_dummy: false,
    nationality_code: getNationality(),
    language: resolveLocaleCode(loadLanguage() || 'ko'),
  };
  // 기존 행이 있는데 nickname이 명시적으로 전달되지 않았으면 nickname 필드를 아예 건드리지 않는다.
  // (글쓰기 흐름 등에서 upsertAndFetchMyAppUser()를 인자 없이 호출할 때마다
  //  user_metadata의 오래된 닉네임 값으로 방금 바꾼 닉네임이 덮어써지던 버그 수정)
  if (explicitNick || !linkedId) {
    row.nickname = explicitNick || user.user_metadata?.nickname || user.email?.split('@')[0] || 'Mingo';
  }
  if (consents && typeof consents === 'object') {
    if (consents.consentPrivacy != null) row.consent_privacy = !!consents.consentPrivacy;
    if (consents.consentMarketing != null) row.consent_marketing = !!consents.consentMarketing;
    if (consents.consentLocation != null) row.consent_location = !!consents.consentLocation;
    if (consents.birthDate) {
      row.birth_date = consents.birthDate;
      row.age_14_confirmed = true;
    }
    Object.assign(row, buildConsentTimestampFields(consents));
  }

  if (linkedId) {
    const { error } = await supabase.from('users').update(row).eq('id', linkedId);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('users').upsert(row, { onConflict: 'auth_id' });
    if (error) throw error;
  }
  void sendToAutomation('user.profile_upsert', { authUserId: user.id });
}

/** Removes app user row and owned data (RPC). Does not delete auth.users — sign out after. */
export async function deleteMyAccountDataFromServer() {
  const supabase = getSupabase();
  if (!supabase) return { ok: false, error: 'no_db' };
  try {
    assertOnlineOrThrow();
  } catch {
    return { ok: false, error: 'offline' };
  }
  const { error } = await supabase.rpc('delete_my_account_data');
  if (error) return { ok: false, error: error.message || 'rpc' };
  return { ok: true };
}

/** 프로필 upsert 후 `public.users` 행을 짧게 재시도하며 조회 */
export async function upsertAndFetchMyAppUser(nickname, consents) {
  const sb = getSupabase();
  if (!sb) return null;
  assertOnlineOrThrow();
  await upsertUserProfileFromAuth(nickname, consents);
  let me = await fetchMyAppUserRow();
  for (let i = 0; i < 4 && !me?.id; i += 1) {
    await new Promise((r) => {
      window.setTimeout(r, 160);
    });
    me = await fetchMyAppUserRow();
  }
  return me;
}

/**
 * 로그인 사용자의 `users.language`를 화면 선택값(localStorage 1차)으로 미러링.
 * 비로그인·오프라인·미설정은 no-op. 실패해도 무시(localStorage가 1차 소스).
 * @param {string} [code] resolveLocaleCode 입력 코드
 */
export async function syncMyLanguageToDb(code) {
  if (!isSupabaseConfigured || !isAppOnline()) return;
  const supabase = getSupabase();
  if (!supabase) return;
  try {
    const user = await getAuthedSupabaseUser(supabase);
    if (!user) return;
    const lang = resolveLocaleCode(code);
    await supabase.from('users').update({ language: lang }).eq('auth_id', user.id);
  } catch {
    /* 미러링 실패는 무시 */
  }
}

/** 연락처 인증 요청 기록 (n8n 웹훅 등 후속 처리용) */
export async function requestPhoneVerification(userId, phone) {
  if (!isSupabaseConfigured || !userId) return;
  const supabase = getSupabase();
  if (!supabase) return;
  assertOnlineOrThrow();
  const { error } = await supabase.from('automation_logs').insert({
    type: 'phone_verification',
    target_id: userId,
    target_type: 'user',
    channel: 'sms',
    payload: { phone, requested_at: new Date().toISOString() },
    status: 'pending',
  });
  if (error) throw error;
  void sendToAutomation('verification.phone_requested', { userId, hasPhone: !!phone });
}

/** 카카오 인증 요청 기록 */
export async function requestKakaoVerification(userId, kakaoId) {
  if (!isSupabaseConfigured || !userId) return;
  const supabase = getSupabase();
  if (!supabase) return;
  assertOnlineOrThrow();
  const { error } = await supabase.from('automation_logs').insert({
    type: 'kakao_verification',
    target_id: userId,
    target_type: 'user',
    channel: 'kakao',
    payload: { kakao_id: kakaoId, requested_at: new Date().toISOString() },
    status: 'pending',
  });
  if (error) throw error;
  void sendToAutomation('verification.kakao_requested', { userId, hasKakaoId: !!kakaoId });
}

/** 잘로(Zalo) 인증 요청 기록 */
export async function requestZaloVerification(userId, zaloId) {
  if (!isSupabaseConfigured || !userId) return;
  const supabase = getSupabase();
  if (!supabase) return;
  assertOnlineOrThrow();
  const { error } = await supabase.from('automation_logs').insert({
    type: 'zalo_verification',
    target_id: userId,
    target_type: 'user',
    channel: 'zalo',
    payload: { zalo_id: zaloId, requested_at: new Date().toISOString() },
    status: 'pending',
  });
  if (error) throw error;
  void sendToAutomation('verification.zalo_requested', { userId, hasZaloId: !!zaloId });
}

/** 파트너 알림 설정 저장 */
export async function upsertPartnerNotification(partnerId, { channels, kakaoId, phone }) {
  if (!isSupabaseConfigured || !partnerId) return;
  const supabase = getSupabase();
  if (!supabase) return;
  assertOnlineOrThrow();
  const { error } = await supabase.from('partner_notifications').upsert(
    {
      partner_id: partnerId,
      channels: channels ?? [],
      kakao_id: kakaoId ?? null,
      phone: phone ?? null,
      is_active: true,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'partner_id' },
  );
  if (error) throw error;
}

/**
 * @param {string} userId
 * @returns {Promise<{
 *   user_id: string,
 *   nickname: string,
 *   avatar_url: string|null,
 *   region: string,
 *   manner_temperature: number,
 *   manner_signal_count: number,
 *   follower_count: number,
 *   following_count: number,
 *   is_following: boolean,
 *   is_self: boolean,
 *   friendship_status: string,
 *   friendship_id: string|null
 * }|null>}
 */
export async function fetchPublicUserProfile(userId) {
  if (!userId) return null;
  const supabase = getSupabase();
  if (!supabase) return null;
  const { data, error } = await supabase.rpc('get_public_user_profile', { p_user_id: userId });
  if (error) throw new Error(formatApiError(error));
  const row = Array.isArray(data) ? data[0] : data;
  return row || null;
}

/**
 * @param {string} targetUserId
 * @param {boolean} shouldFollow
 * @returns {Promise<{ success: boolean, mutual?: boolean, became_friend?: boolean }>}
 */
export async function setFollowUser(targetUserId, shouldFollow) {
  assertOnlineOrThrow();
  const supabase = getSupabase();
  if (!supabase) throw new Error('SUPABASE_MISSING');
  const rpcName = shouldFollow ? 'follow_user' : 'unfollow_user';
  const { data, error } = await supabase.rpc(rpcName, {
    p_target_user_id: targetUserId,
  });
  if (error) throw new Error(formatApiError(error));
  const result = data && typeof data === 'object' ? data : {};
  if (result.success === false) {
    const e = new Error(String(result.error || 'FOLLOW_FAILED'));
    e.code = result.error;
    throw e;
  }
  return result;
}

/** @param {string} userId @returns {Promise<{ followers: number, following: number }>} */
export async function getFollowCounts(userId) {
  assertOnlineOrThrow();
  const supabase = getSupabase();
  if (!supabase) throw new Error('SUPABASE_MISSING');
  const { data, error } = await supabase.rpc('get_follow_counts', {
    p_user_id: userId,
  });
  if (error) throw new Error(formatApiError(error));
  const row = data && typeof data === 'object' ? data : {};
  return {
    followers: Number(row.followers) || 0,
    following: Number(row.following) || 0,
  };
}

/**
 * @param {string} targetUserId
 * @param {number} [limit]
 * @param {number} [offset]
 * @returns {Promise<Array<{ user_id: string, nickname: string, avatar_url: string|null, followed_at: string, is_mutual: boolean }>>}
 */
export async function getFollowers(targetUserId, limit = 50, offset = 0) {
  assertOnlineOrThrow();
  const supabase = getSupabase();
  if (!supabase) throw new Error('SUPABASE_MISSING');
  const { data, error } = await supabase.rpc('get_followers', {
    p_target_user_id: targetUserId,
    p_limit: limit,
    p_offset: offset,
  });
  if (error) throw new Error(formatApiError(error));
  return Array.isArray(data) ? data : [];
}

/**
 * @param {string} targetUserId
 * @param {number} [limit]
 * @param {number} [offset]
 * @returns {Promise<Array<{ user_id: string, nickname: string, avatar_url: string|null, followed_at: string, is_mutual: boolean }>>}
 */
export async function getFollowing(targetUserId, limit = 50, offset = 0) {
  assertOnlineOrThrow();
  const supabase = getSupabase();
  if (!supabase) throw new Error('SUPABASE_MISSING');
  const { data, error } = await supabase.rpc('get_following', {
    p_target_user_id: targetUserId,
    p_limit: limit,
    p_offset: offset,
  });
  if (error) throw new Error(formatApiError(error));
  return Array.isArray(data) ? data : [];
}

/**
 * @param {number} [limit]
 * @param {number} [offset]
 * @returns {Promise<Array<{
 *   user_id: string,
 *   nickname: string,
 *   handle: string|null,
 *   avatar_url: string|null,
 *   region: string|null,
 *   candidate_kind: 'user'|'business',
 *   business_name: string|null,
 *   reason_text: 'same_city'|'shared_interest'|'mutual_friend'|'recent_activity'|string,
 *   score: number
 * }>>}
 */
export async function getSuggestedConnections(limit = 20, offset = 0) {
  assertOnlineOrThrow();
  const supabase = getSupabase();
  if (!supabase) throw new Error('SUPABASE_MISSING');
  const { data, error } = await supabase.rpc('get_suggested_connections', {
    p_limit: Math.max(1, Math.min(50, Number(limit) || 20)),
    p_offset: Math.max(0, Number(offset) || 0),
  });
  if (error) throw new Error(formatApiError(error));
  return Array.isArray(data) ? data : [];
}
